import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import { useState } from 'react';
import { Alert, Badge, Button, Card, Modal, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { selectPatient } from 'redux/features/auth/authSlice';
import { buscaAgendaDoPaciente } from 'services/agenda';
import useSWR from 'swr';
import { isInfrastructureError } from 'utils/apiErrors';
import { formatarHora } from 'utils/helpers';

export default function AgendaPaciente() {
  const patient = useSelector(selectPatient);
  const codPac = patient?.codPac;
  const [agendaSelecionada, setAgendaSelecionada] = useState(null);

  const {
    data: agenda = [],
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR(codPac ? ['agenda-paciente', codPac] : null, ([, id]) => buscaAgendaDoPaciente(id), {
    shouldRetryOnError: true,
    errorRetryCount: 2,
    errorRetryInterval: 3000,
  });

  const temAgenda = agenda.length > 0;
  const exibirErro = Boolean(error) && !isValidating;
  const erroInfraestrutura = isInfrastructureError(error);

  function getStatusBadgeVariant(situacao) {
    const status = situacao?.toUpperCase();
    if (status?.includes('CANCEL') || status?.includes('FALTA')) return 'danger';
    if (status?.includes('CONFIRM') || status?.includes('REALIZAD')) return 'success';
    if (status?.includes('ATEND')) return 'info';
    return 'primary';
  }

  return (
    <>
      <Card className="mt-2">
        <Card.Header as="h5" className="bg-primary text-white py-3">
          Meus Agendamentos
        </Card.Header>

        <Card.Body className={isLoading || (!temAgenda && !exibirErro) ? 'p-4' : 'p-0'}>
          {isLoading && !temAgenda ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" role="status" />
              <p className="mt-2 text-muted small mb-0">Carregando seus agendamentos...</p>
            </div>
          ) : (
            <>
              {exibirErro &&
                (erroInfraestrutura ? (
                  <div className="p-3">
                    <ServiceUnavailableAlert
                      error={error}
                      stale={temAgenda}
                      onRetry={() => mutate()}
                    />
                  </div>
                ) : (
                  <Alert variant="danger" className="text-center m-3">
                    Erro ao carregar agendamentos.
                    <div className="mt-2">
                      <Button variant="outline-danger" size="sm" onClick={() => mutate()}>
                        Tentar novamente
                      </Button>
                    </div>
                  </Alert>
                ))}

              {temAgenda ? (
                <Table responsive striped hover size="sm" className="align-middle mb-0">
                  <thead className="table-light border-bottom">
                    <tr>
                      <th className="ps-3 py-2">Atividade</th>
                      <th className="py-2">Data</th>
                      <th className="py-2">Horário</th>
                      <th className="py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agenda.map((item) => (
                      <tr
                        key={item.CODAGENDA}
                        role="button"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setAgendaSelecionada(item)}
                      >
                        <td className="ps-3 fw-medium text-primary">{item.ATIVIDADE}</td>
                        <td>{item.DATAAGENDA_FORMATADA}</td>
                        <td>{formatarHora(item.HORARIO)}</td>
                        <td className="text-center">
                          <Badge bg={getStatusBadgeVariant(item.SITUACAO || item.SITUACAOAGENDA)}>
                            {item.SITUACAO || item.SITUACAOAGENDA || 'Agendada'}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : !exibirErro ? (
                <Alert variant="info" className="text-center mb-0">
                  Você não possui agendamentos cadastrados.
                </Alert>
              ) : null}
            </>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!agendaSelecionada} onHide={() => setAgendaSelecionada(null)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title as="h5" className="text-primary">
            Detalhes do Agendamento
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {agendaSelecionada && (
            <div className="d-flex flex-column gap-2">
              <div>
                <strong>Atividade:</strong> {agendaSelecionada.ATIVIDADE}
              </div>
              <div>
                <strong>Data:</strong> {agendaSelecionada.DATAAGENDA_FORMATADA}
              </div>
              <div>
                <strong>Horário:</strong> {formatarHora(agendaSelecionada.HORARIO)}
              </div>

              {agendaSelecionada.NOMEMED && (
                <div>
                  <strong>Médico(a):</strong> {agendaSelecionada.NOMEMED}
                </div>
              )}

              {agendaSelecionada.NOMECONV && (
                <div>
                  <strong>Convênio:</strong> {agendaSelecionada.NOMECONV}
                </div>
              )}

              {agendaSelecionada.LOCAL && (
                <div>
                  <strong>Local/Unidade:</strong> {agendaSelecionada.LOCAL}
                </div>
              )}

              <div>
                <strong>Situação:</strong>{' '}
                <Badge bg={getStatusBadgeVariant(agendaSelecionada.SITUACAO)}>
                  {agendaSelecionada.SITUACAO || 'Agendada'}
                </Badge>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setAgendaSelecionada(null)}>
            Fechar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
